<button
  type="button"
  class="dtrig"
  [class.is-open]="open"
  [class.has-unread]="unreadTotal > 0"
  aria-label="Desk chat. Shortcut Alt plus D."
  title="Desk chat (Alt+D)"
  [attr.aria-expanded]="open"
  (click)="toggle($event)"
>
  <i class="pi pi-comments"></i>
  @if (unreadTotal > 0) {
    <span class="dtrig__dot">{{ unreadTotal > 9 ? '9+' : unreadTotal }}</span>
  }
</button>

@if (open) {
  <div class="dpanel" role="dialog" aria-label="Desk chat" (click)="$event.stopPropagation()">
    <div class="dpanel__glow" aria-hidden="true"></div>

    <header class="dpanel__head">
      @if (activeClinicId && threads.length > 1) {
        <button
          type="button"
          class="dpanel__back"
          (click)="backToThreads($event)"
          [attr.aria-label]="otherUnread ? otherUnread + ' unread in other clinics' : 'All clinics'"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          @if (otherUnread > 0) {
            <span class="dpanel__back-count">{{ otherUnread > 9 ? '9+' : otherUnread }}</span>
          }
        </button>
      }
      <div class="dpanel__title">
        <p class="dpanel__eyebrow">Desk chat</p>
        <h2>{{ activeClinicId ? activeThreadName() : 'Clinics' }}</h2>
      </div>
      <span class="dpanel__shortcut" aria-hidden="true"><kbd>Alt</kbd><kbd>D</kbd></span>
      @if (!activeClinicId && unreadTotal > 0) {
        <span class="dpanel__count">{{ unreadTotal }}</span>
      }
    </header>

    @if (!activeClinicId) {
      <div class="dpanel__threads">
        @for (thread of threads; track thread.id) {
          <button type="button" class="dthread" (click)="selectThread(thread, $event)">
            <span class="dthread__mark" aria-hidden="true">{{ threadInitial(thread.name) }}</span>
            <span class="dthread__copy">
              <span class="dthread__name">{{ thread.name }}</span>
              <span class="dthread__preview">{{ thread.last_message?.body || 'No messages yet' }}</span>
            </span>
            <span class="dthread__side">
              @if (thread.last_message?.created_at) {
                <span class="dthread__time">{{ formatTime(thread.last_message?.created_at) }}</span>
              }
              @if (thread.unread_count > 0) {
                <span class="dthread__unread">{{ thread.unread_count }}</span>
              }
            </span>
          </button>
        } @empty {
          <p class="dpanel__empty">No clinic chat is available for this login.</p>
        }
      </div>
    } @else {
      <div class="dpanel__messages" #messageList>
        @if (loadingMessages && !messages.length) {
          <p class="dpanel__empty">Loading messages…</p>
        } @else if (!messages.length) {
          <p class="dpanel__empty">Write the desk, or attach the patient on screen when the note is about them.</p>
        }
        @for (message of messages; track message.id) {
          <article class="dmsg" [class.is-mine]="message.mine">
            <header class="dmsg__meta">
              <span class="dmsg__who">{{ message.mine ? 'You' : message.sender_name }}</span>
              <span class="dmsg__role">{{ message.sender_role === 'doctor' ? 'Doctor' : 'Reception' }}</span>
              <span class="dmsg__time">{{ formatTime(message.created_at) }}</span>
            </header>
            <div class="dmsg__bubble">
              <p class="dmsg__body">{{ message.body }}</p>
              @if (message.patient) {
                <button type="button" class="dmsg__patient" (click)="openAttachedPatient(message, $event)">
                  <i class="pi pi-user" aria-hidden="true"></i>
                  <span class="dmsg__patient-copy">
                    <span class="dmsg__patient-name">{{ message.patient.name }}</span>
                    <span class="dmsg__patient-meta">
                      {{ message.patient.patient_number }}
                      @if (message.patient.card_number) {
                        <span>Card {{ message.patient.card_number }}</span>
                      }
                    </span>
                  </span>
                  <span class="dmsg__open">Open</span>
                </button>
              }
            </div>
          </article>
        }
      </div>

      <div class="dpanel__composer">
        <div class="dpanel__quick" aria-label="Quick lines">
          @for (line of quickLines; track line.text) {
            <button type="button" class="dquick" [disabled]="sending" (click)="sendQuick(line, $event)">
              {{ line.text }}
            </button>
          }
        </div>

        <form class="dpanel__form" (submit)="$event.preventDefault(); send($event)">
          @if (attachableName) {
            <button
              type="button"
              class="dattach"
              [class.is-on]="includePatient"
              (click)="toggleIncludePatient($event)"
            >
              <i class="pi" [ngClass]="includePatient ? 'pi-check' : 'pi-user-plus'" aria-hidden="true"></i>
              <span>{{ includePatient ? 'Attached' : 'Attach' }} {{ attachableName }}</span>
            </button>
          }
          <div class="dpanel__row">
            <textarea
              name="desk_chat_body"
              [(ngModel)]="draft"
              rows="2"
              maxlength="500"
              placeholder="Write a message"
              (keydown)="onDraftKeydown($event)"
            ></textarea>
            <button type="submit" class="dsend" [disabled]="sending || !draft.trim()" aria-label="Send">
              <i class="pi" [ngClass]="sending ? 'pi-spin pi-spinner' : 'pi-send'" aria-hidden="true"></i>
            </button>
          </div>
        </form>
      </div>
    }
  </div>
}
